import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Heart, Bookmark, MessageCircle, Search, Filter,
  ArrowRight, Star, Package, Palette, Users, TrendingUp,
  CheckCircle, Globe, CreditCard, Percent, Building, Frame
} from 'lucide-react';
import { getImageUrl, handleImageError } from '@/utils/helpers';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// 作品分类 - 使用翻译键
const REGIONS = [
  { id: 'all', labelKey: 'artCanvas.regions.all' },
  { id: 'central-asia', labelKey: 'artCanvas.regions.centralAsia' },
  { id: 'caucasus', labelKey: 'artCanvas.regions.caucasus' },
  { id: 'east-asia', labelKey: 'artCanvas.regions.eastAsia' },
  { id: 'europe', labelKey: 'artCanvas.regions.europe' },
  { id: 'middle-east', labelKey: 'artCanvas.regions.middleEast' },
  { id: 'other', labelKey: 'artCanvas.regions.other' },
];

const ArtCanvasPage = () => {
  const [artworks, setArtworks] = useState([]);
  const [artists, setArtists] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({ artworks: 0, artists: 0, users: 0 });
  const [settings, setSettings] = useState({
    hero_background_image: 'https://static.prod-images.emergentagent.com/jobs/6cd3f574-04ed-4fa3-a707-ad66f9619473/images/a7b5fe89c7a0f11bb6e942c90e2f580761dc634c670350178074371452e44821.png',
    tote_bag_image: '',
    hero_title: 'ArtCanvas',
    hero_subtitle: '发现独特的艺术之美',
    tote_bag_title: '把你的作品变成艺术品',
    tote_bag_description: '我们将你的画作印制在100%有机棉包上，让你的艺术走进日常生活。',
    royalty_percentage: 10,
    tote_bag_features: [
      '100%有机棉，环保可持续',
      '高清数码印刷，色彩持久',
      '多种尺寸可选，全球配送',
      '透明结算，按月支付版税'
    ],
    tote_bag_detail_link: '/about'
  });
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const currentLang = i18n.language;
      const [artworksRes, creatorsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/artworks?lang=${currentLang}`),
        axios.get(`${API}/search/creators?limit=6`),
        axios.get(`${API}/settings/artcanvas`)
      ]);
      setArtworks(artworksRes.data);
      setArtists(creatorsRes.data.creators || []);
      setStats({
        artworks: artworksRes.data.length,
        artists: creatorsRes.data.total || 0,
        users: 1000 // 示例数据
      });
      if (settingsRes.data) {
        setSettings(prev => ({ ...prev, ...settingsRes.data }));
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // 点赞
  const handleLike = async (e, artworkId) => {
    e.stopPropagation();
    if (!user) {
      toast.error(t('artCanvas.loginFirst'));
      return;
    }
    try {
      const res = await axios.post(`${API}/artworks/${artworkId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // 立即更新本地状态
      setArtworks(prev => prev.map(artwork => {
        if (artwork.id === artworkId) {
          return {
            ...artwork,
            is_liked: res.data.liked,
            likes_count: res.data.liked 
              ? (artwork.likes_count || 0) + 1 
              : Math.max((artwork.likes_count || 0) - 1, 0)
          };
        }
        return artwork;
      }));
      toast.success(res.data.liked ? t('artCanvas.liked') : t('artCanvas.unliked'));
    } catch (err) {
      console.error('Like failed:', err);
      toast.error(t('artCanvas.operationFailed'));
    }
  };

  // 收藏
  const handleSave = async (e, artworkId) => {
    e.stopPropagation();
    if (!user) {
      toast.error(t('artCanvas.loginFirst'));
      return;
    }
    try {
      const res = await axios.post(`${API}/artworks/${artworkId}/save`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // 立即更新本地状态
      setArtworks(prev => prev.map(artwork => {
        if (artwork.id === artworkId) {
          return {
            ...artwork,
            is_saved: res.data.saved,
            saves_count: res.data.saved 
              ? (artwork.saves_count || 0) + 1 
              : Math.max((artwork.saves_count || 0) - 1, 0)
          };
        }
        return artwork;
      }));
      toast.success(res.data.saved ? t('artCanvas.saved') : t('artCanvas.unsaved'));
    } catch (err) {
      console.error('Save failed:', err);
      toast.error(t('artCanvas.operationFailed'));
    }
  };

  // 过滤作品
  const filteredArtworks = artworks.filter(artwork => {
    const artRegion = artwork.region || '';
    const matchRegion = selectedCategory === 'all' || artRegion === selectedCategory;
    const matchSearch = !searchTerm || 
      artwork.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      artwork.artist?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchRegion && matchSearch;
  });

  const getLocalizedField = (item, field) => {
    if (!item) return '';
    const lang = i18n.language;
    if (lang === 'en' && item[`${field}_en`]) return item[`${field}_en`];
    if (lang === 'ru' && item[`${field}_ru`]) return item[`${field}_ru`];
    return item[field] || '';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-amber-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-amber-50 pt-16">
      {/* Hero Section - 美术馆背景 */}
      <section className="relative h-[calc(100vh-64px)] flex items-center justify-center overflow-hidden">
        {/* 动态背景图 - 默认为Lady Godiva by John Collier */}
        <div className="absolute inset-0">
          <img loading="lazy"
            src={settings.hero_background_image || "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e5/John_Collier_-_Lady_Godiva_-_c_1898_-_Herbert_Art_Gallery_and_Museum.jpg/1280px-John_Collier_-_Lady_Godiva_-_c_1898_-_Herbert_Art_Gallery_and_Museum.jpg"}
            alt="Lady Godiva by John Collier"
            className="w-full h-full object-cover object-[center_10%]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50" />
        </div>
        
        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
          <h1 className="text-6xl md:text-8xl font-light tracking-tight text-white mb-6">
            ArtCanvas
          </h1>
          <p className="text-xl md:text-2xl text-white/80 font-light mb-4">
            {t('artCanvas.hero.subtitle')}
          </p>
          <p className="text-lg text-white/60 max-w-2xl mx-auto mb-12">
            {t('artCanvas.hero.description')}
          </p>
          
          {/* Stats */}
          <div className="flex justify-center gap-16 mb-12">
            <div className="text-center">
              <div className="text-4xl font-light text-white">{stats.artworks}+</div>
              <div className="text-sm text-white/60 mt-1">{t('artCanvas.stats.artworks')}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-light text-white">{stats.artists}+</div>
              <div className="text-sm text-white/60 mt-1">{t('artCanvas.stats.artists')}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-light text-white">{stats.users}+</div>
              <div className="text-sm text-white/60 mt-1">{t('artCanvas.stats.users')}</div>
            </div>
          </div>

          <div className="flex justify-center gap-4">
            <Button 
              className="bg-white text-black hover:bg-white/90 rounded-full px-8 py-6 text-lg"
              onClick={() => document.getElementById('artworks-section').scrollIntoView({ behavior: 'smooth' })}
            >
              {t('artCanvas.hero.exploreArtworks')}
            </Button>
            <Link to="/creator-center">
              <Button 
                variant="outline" 
                className="rounded-full px-8 py-6 text-lg border-white text-white hover:bg-white hover:text-black"
              >
                {t('artCanvas.hero.becomeArtist')}
              </Button>
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/40 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-white/60 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Artworks Section */}
      <section id="artworks-section" className="py-16 px-4 bg-amber-50/50">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-light text-amber-900 mb-2">{t('artCanvas.featuredWorks')}</h2>
            <p className="text-amber-700/70 text-sm">{t('artCanvas.featuredWorksDesc')}</p>
          </div>

          {/* Search & Filter */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-12">
            <div className="relative w-full md:w-96">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-500" />
              <Input
                placeholder={t('artCanvas.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 pr-4 py-3 rounded-full border-amber-200 focus:border-amber-500 focus:ring-amber-500 bg-white"
              />
            </div>
            
            {/* Region Filter */}
            <div className="flex gap-2 overflow-x-auto pb-2 w-full md:w-auto">
              {REGIONS.map(cat => (
                <Button
                  key={cat.id}
                  variant={selectedCategory === cat.id ? 'default' : 'ghost'}
                  className={`rounded-full whitespace-nowrap ${
                    selectedCategory === cat.id 
                      ? 'bg-amber-600 text-white hover:bg-amber-700' 
                      : 'text-amber-700 hover:bg-amber-100'
                  }`}
                  onClick={() => setSelectedCategory(cat.id)}
                >
                  {t(cat.labelKey)}
                </Button>
              ))}
            </div>
          </div>

          {/* Artworks Grid - 小米影像风格：悬停显示信息 */}
          <div className="columns-1 md:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
            {filteredArtworks.map((artwork, index) => (
              <Link
                key={artwork.id}
                to={`/artwork/${artwork.id}`}
                className="break-inside-avoid group block"
              >
                <div className="relative rounded-xl overflow-hidden">
                  {/* 纯净的作品图片 */}
                  <img
                    src={getImageUrl(artwork.image_url)}
                    alt={getLocalizedField(artwork, 'title')}
                    className="w-full object-cover group-hover:scale-105 transition-transform duration-700"
                    style={{ minHeight: '200px' }}
                    loading="lazy"
                    onError={handleImageError}
                  />
                  
                  {/* 悬停时的渐变遮罩和信息 */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300">
                    {/* 顶部操作按钮 - 阻止事件冒泡 */}
                    <div className="absolute top-3 right-3 flex gap-2">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleLike(e, artwork.id);
                        }}
                        className="w-9 h-9 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center hover:bg-white/40 transition-colors"
                      >
                        <Heart className={`w-4 h-4 ${artwork.is_liked ? 'fill-red-500 text-red-500' : 'text-white'}`} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSave(e, artwork.id);
                        }}
                        className="w-9 h-9 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center hover:bg-white/40 transition-colors"
                      >
                        <Bookmark className={`w-4 h-4 ${artwork.is_saved ? 'fill-white text-white' : 'text-white'}`} />
                      </button>
                    </div>
                    
                    {/* 底部信息区 */}
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      {/* 作者信息 */}
                      <div className="flex items-center gap-2 mb-2">
                        {artwork.creator_avatar ? (
                          <img loading="lazy"
                            src={getImageUrl(artwork.creator_avatar)} 
                            alt="" 
                            className="w-6 h-6 rounded-full object-cover border border-white/30"
                          />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                            <Palette className="w-3 h-3 text-white" />
                          </div>
                        )}
                        <span className="text-xs text-white/80">
                          {artwork.creator_name || artwork.artist || t('artCanvas.anonymousArtist')}
                        </span>
                      </div>
                      
                      {/* 作品标题 */}
                      <h3 className="text-white font-medium mb-1 line-clamp-1">
                        {getLocalizedField(artwork, 'title')}
                      </h3>
                      
                      {/* 价格和统计 */}
                      <div className="flex items-center justify-between">
                        {artwork.price ? (
                          <span className="text-white font-medium">
                            ¥{artwork.price.toLocaleString()}
                          </span>
                        ) : (
                          <span className="text-white/60 text-sm">{t('artCanvas.notPriced')}</span>
                        )}
                        <div className="flex items-center gap-3 text-white/60 text-xs">
                          <span className="flex items-center gap-1">
                            <Heart className="w-3 h-3" /> {artwork.likes_count || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <Bookmark className="w-3 h-3" /> {artwork.saves_count || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filteredArtworks.length === 0 && (
            <div className="text-center py-20">
              <p className="text-amber-500 text-lg">{t('artCanvas.noArtworks')}</p>
            </div>
          )}

          {/* Load More */}
          {filteredArtworks.length > 0 && (
            <div className="text-center mt-16">
              <Link to="/artworks">
                <Button 
                  variant="outline" 
                  className="rounded-full px-8 py-6 border-amber-600 text-amber-700 hover:bg-amber-600 hover:text-white"
                  data-testid="view-more-artworks-btn"
                >
                  {t('artCanvas.viewMoreArtworks')} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Art Community Hub - 艺术社区分类入口 */}
      <section className="py-16 px-4 bg-amber-50/30" id="art-community">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-light text-amber-900 mb-2">{t('artCanvas.community.title')}</h2>
            <p className="text-amber-700/70 text-sm">{t('artCanvas.community.subtitle')}</p>
          </div>

          {/* 四大分类入口 */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* 艺术家/画家 */}
            <Link
              to="/users/artist"
              className="group block bg-white rounded-3xl overflow-hidden hover:shadow-xl transition-all duration-500"
              data-testid="art-community-artists"
            >
              <div className="relative aspect-[4/3]">
                <img loading="lazy"
                  src="https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?w=600&q=80"
                  alt={t('userTypes.artist')}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mb-3">
                    <Palette className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-light text-white mb-1">{t('userTypes.artist')}</h3>
                  <p className="text-white/70 text-sm">{t('artCanvas.community.artistDesc')}</p>
                </div>
              </div>
            </Link>

            {/* 收藏家 */}
            <Link
              to="/users/collector"
              className="group block bg-white rounded-3xl overflow-hidden hover:shadow-xl transition-all duration-500"
              data-testid="art-community-collectors"
            >
              <div className="relative aspect-[4/3]">
                <img loading="lazy"
                  src="https://images.unsplash.com/photo-1582555172866-f73bb12a2ab3?w=600&q=80"
                  alt={t('userTypes.collector')}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mb-3">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-light text-white mb-1">{t('userTypes.collector')}</h3>
                  <p className="text-white/70 text-sm">{t('artCanvas.community.collectorDesc')}</p>
                </div>
              </div>
            </Link>

            {/* 美术馆 */}
            <Link
              to="/users/museum"
              className="group block bg-white rounded-3xl overflow-hidden hover:shadow-xl transition-all duration-500"
              data-testid="art-community-museums"
            >
              <div className="relative aspect-[4/3]">
                <img loading="lazy"
                  src="https://images.unsplash.com/photo-1554907984-15263bfd63bd?w=600&q=80"
                  alt={t('userTypes.museum')}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mb-3">
                    <Building className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-light text-white mb-1">{t('userTypes.museum')}</h3>
                  <p className="text-white/70 text-sm">{t('artCanvas.community.museumDesc')}</p>
                </div>
              </div>
            </Link>

            {/* 画展专题 */}
            <Link
              to="/exhibitions"
              className="group block bg-white rounded-3xl overflow-hidden hover:shadow-xl transition-all duration-500"
              data-testid="art-community-exhibitions"
            >
              <div className="relative aspect-[4/3]">
                <img loading="lazy"
                  src="https://images.unsplash.com/photo-1577720643272-265f09367456?w=600&q=80"
                  alt={t('artCanvas.community.exhibitions')}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mb-3">
                    <Frame className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-light text-white mb-1">{t('artCanvas.community.exhibitions')}</h3>
                  <p className="text-white/70 text-sm">{t('artCanvas.community.exhibitionsDesc')}</p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Tote Bag Service Section */}
      <section className="py-24 px-4 bg-amber-50/30">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="relative">
              <div className="aspect-square bg-white rounded-3xl overflow-hidden shadow-2xl border border-amber-100">
                {/* 主图 - 边缘渐变到白色 */}
                <div className="relative w-full h-full">
                  <img loading="lazy"
                    src={getImageUrl(settings.tote_bag_image)}
                    alt={t('artCanvas.toteBag.title')}
                    className="w-full h-full object-cover"
                    data-testid="tote-bag-image"
                    style={{
                      maskImage: 'radial-gradient(ellipse 60% 65% at center, black 40%, transparent 85%)',
                      WebkitMaskImage: 'radial-gradient(ellipse 60% 65% at center, black 40%, transparent 85%)'
                    }}
                  />
                </div>
              </div>
              {/* Floating Badge */}
              <div className="absolute -bottom-6 -right-6 bg-amber-600 text-white rounded-2xl p-6 shadow-2xl" data-testid="royalty-badge">
                <div className="text-3xl font-light">{settings.royalty_percentage}%</div>
                <div className="text-sm text-amber-100">{t('artCanvas.toteBag.royalty')}</div>
                <div className="text-xs text-amber-200">{t('artCanvas.toteBag.perSale')}</div>
              </div>
            </div>

            {/* Content */}
            <div>
              <Badge className="bg-amber-100 text-amber-700 rounded-full mb-6">{t('artCanvas.toteBag.customService')}</Badge>
              <h2 className="text-4xl font-light text-amber-900 mb-6">
                {t('artCanvas.toteBag.title')}
              </h2>
              <p className="text-amber-700/80 text-lg mb-8 leading-relaxed">
                {t('artCanvas.toteBag.description', { percentage: settings.royalty_percentage })}
              </p>

              {/* Features */}
              <div className="space-y-4 mb-10">
                <div className="flex items-center gap-3 text-amber-700">
                  <CheckCircle className="w-5 h-5 text-amber-600" />
                  <span>{t('artCanvas.toteBag.feature1')}</span>
                </div>
                <div className="flex items-center gap-3 text-amber-700">
                  <CheckCircle className="w-5 h-5 text-amber-600" />
                  <span>{t('artCanvas.toteBag.feature2')}</span>
                </div>
                <div className="flex items-center gap-3 text-amber-700">
                  <CheckCircle className="w-5 h-5 text-amber-600" />
                  <span>{t('artCanvas.toteBag.feature3')}</span>
                </div>
                <div className="flex items-center gap-3 text-amber-700">
                  <CheckCircle className="w-5 h-5 text-amber-600" />
                  <span>{t('artCanvas.toteBag.feature4')}</span>
                </div>
              </div>

              {/* Revenue Split */}
              <div className="bg-amber-100/50 rounded-2xl p-6 mb-8">
                <div className="text-sm text-amber-600 mb-3">{t('artCanvas.toteBag.revenueSplit')}</div>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="h-3 bg-amber-200 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-600 rounded-full" style={{ width: `${settings.royalty_percentage}%` }}></div>
                    </div>
                  </div>
                  <div className="text-sm">
                    <span className="text-amber-600 font-medium">{t('artCanvas.toteBag.artistShare', { percentage: settings.royalty_percentage })}</span>
                    <span className="text-amber-400 mx-2">|</span>
                    <span className="text-amber-400">{t('artCanvas.toteBag.platformShare', { percentage: 100 - settings.royalty_percentage })}</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <Link to="/custom-service">
                  <Button 
                    className="bg-amber-600 text-white hover:bg-amber-700 rounded-full px-8 py-6"
                    data-testid="custom-now-btn"
                  >
                    {t('artCanvas.toteBag.customNow')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 bg-gradient-to-br from-amber-500 to-amber-600 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-light mb-4">{t('artCanvas.features.title')}</h2>
            <p className="text-white/70">{t('artCanvas.features.subtitle')}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <Palette className="w-8 h-8" />,
                titleKey: 'artCanvas.features.showcase.title',
                descKey: 'artCanvas.features.showcase.desc'
              },
              {
                icon: <Heart className="w-8 h-8" />,
                titleKey: 'artCanvas.features.social.title',
                descKey: 'artCanvas.features.social.desc'
              },
              {
                icon: <CreditCard className="w-8 h-8" />,
                titleKey: 'artCanvas.features.sales.title',
                descKey: 'artCanvas.features.sales.desc'
              },
              {
                icon: <Package className="w-8 h-8" />,
                titleKey: 'artCanvas.features.merchandise.title',
                descKey: 'artCanvas.features.merchandise.desc'
              }
            ].map((feature, i) => (
              <div key={i} className="text-center p-8 border border-white/30 rounded-2xl hover:border-white/60 hover:bg-white/10 transition-all duration-300">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-white/20 flex items-center justify-center">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-medium mb-3">{t(feature.titleKey)}</h3>
                <p className="text-white/70">{t(feature.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-4 bg-amber-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl font-light text-amber-900 mb-6">
            {t('artCanvas.cta.title')}
          </h2>
          <p className="text-xl text-amber-700/70 mb-12">
            {t('artCanvas.cta.subtitle')}
          </p>
          
          <div className="flex justify-center gap-8 mb-12">
            <div className="text-center">
              <div className="text-3xl font-light text-amber-900">¥0</div>
              <div className="text-sm text-amber-500">{t('artCanvas.cta.joinFee')}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-light text-amber-900">10%</div>
              <div className="text-sm text-amber-500">{t('artCanvas.cta.royaltyIncome')}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-light text-amber-900">24h</div>
              <div className="text-sm text-amber-500">{t('artCanvas.cta.reviewTime')}</div>
            </div>
          </div>

          <Link to={user ? '/creator-center' : '/login'}>
            <Button 
              className="bg-amber-600 text-white hover:bg-amber-700 rounded-full px-12 py-6 text-lg"
            >
              {user ? t('artCanvas.cta.goToCreatorCenter') : t('artCanvas.cta.freeRegister')}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ArtCanvasPage;
